import { parseISO } from 'date-fns/parseISO'
import { GridColDef } from '@mui/x-data-grid-pro'
import { HeaderCell } from '../components/trendstable/HeaderCell'
import { CategoryCell } from '../components/trendstable/CategoryCell'
import { amountComparator, categoryComparator } from '../../core/utils/TrendsUtil'
import { AmountCell } from '../components/trendstable/AmountCell'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'
import { DetailedCategory, type MonthlyAmount, TrendsCopy } from '../../core'
import { buildDynamicCopy } from '../../core/utils/Localization'

const amountsReducer = (records: Record<string, number>, monthlyAmount: MonthlyAmount) => {
  const monthHeader = formatDate(parseISO(monthlyAmount.isoDate), DATE_FORMATS_INTL.MONTH_LONG)
  records[monthHeader] = monthlyAmount.amount
  return records
}

const totalReducer = (monthHeaders: string[]) => (sum: number, ma: MonthlyAmount) => {
  const monthHeader = formatDate(parseISO(ma.isoDate), DATE_FORMATS_INTL.MONTH_LONG)
  return sum + (monthHeaders.includes(monthHeader) ? ma.amount : 0)
}

export const generateMonthHeaders = (start: Date, end: Date): string[] => {
  const result: string[] = []
  const current = new Date(start)

  while (current <= end) {
    const formattedDate = formatDate(current, DATE_FORMATS_INTL.MONTH_LONG)
    result.push(formattedDate)
    current.setMonth(current.getMonth() + 1)
  }

  return result
}

export const getTrendsTableColumns = (monthHeaders: string[], copy: TrendsCopy) => {
  const categoryColumn: GridColDef = {
    field: 'category',
    flex: 1,
    headerClassName: 'trends-table-header',
    headerName: copy.category_title,
    minWidth: 210,
    renderHeader: HeaderCell,
    renderCell: CategoryCell,
    sortable: true,
    sortComparator: categoryComparator,
    type: 'string',
  }

  const monthColumns: GridColDef[] = monthHeaders.map((monthHeader) => ({
    field: monthHeader,
    flex: 1,
    headerClassName: 'trends-table-header',
    headerName: monthHeader,
    minWidth: 100,
    renderHeader: HeaderCell,
    renderCell: AmountCell,
    align: 'center',
    headerAlign: 'center',
    sortable: true,
    hideSortIcons: false,
    type: 'number',
    sortComparator: amountComparator,
    valueGetter: (_value: unknown, row: TrendsTableRow) => row.monthlyAmounts?.[monthHeader] ?? 0,
  }))

  const totalColumn: GridColDef = {
    field: 'total',
    flex: 1,
    headerClassName: 'trends-table-header',
    headerName: copy.table_column_total,
    minWidth: 100,
    renderHeader: HeaderCell,
    renderCell: AmountCell,
    align: 'center',
    headerAlign: 'center',
    sortable: true,
    hideSortIcons: false,
    type: 'number',
    sortComparator: amountComparator,
  }

  return [categoryColumn, ...monthColumns, totalColumn]
}

export type TrendsTableRow = {
  id: string
  category: string
  category_guid: string
  isParent: boolean
  top_level_category_guid: string
  total: number
  monthlyAmounts: Record<string, number>
}

export const getTrendsTableRows = (
  categories: DetailedCategory[],
  copy: TrendsCopy,
  isIncome: boolean,
  monthHeaders: string[],
  parentCategory?: DetailedCategory,
): TrendsTableRow[] => {
  const categoryRows = categories
    .filter((category) => category.is_income === isIncome)
    .map((category) => {
      let amounts = category.totalMonthlyAmounts
      if (parentCategory) {
        amounts = category.monthlyAmounts
      }

      return {
        id: category.guid,
        category: category.name,
        category_guid: category.guid,
        isParent: false,
        top_level_category_guid: category.parent_guid || category.guid,
        total: amounts.reduce(totalReducer(monthHeaders), 0),
        monthlyAmounts: amounts.reduce(amountsReducer, {}),
      }
    })

  // Add General category row when viewing sub-categories
  if (parentCategory && parentCategory.is_income === isIncome) {
    const total = parentCategory.monthlyAmounts.reduce(totalReducer(monthHeaders), 0)
    if (total !== 0) {
      categoryRows.push({
        id: `sub-${parentCategory.guid}`,
        category: buildDynamicCopy(copy.category_general, parentCategory.name) as string,
        category_guid: parentCategory.guid,
        isParent: true,
        top_level_category_guid: parentCategory.guid,
        total,
        monthlyAmounts: parentCategory.monthlyAmounts.reduce(amountsReducer, {}),
      })
    }
  }

  return categoryRows
}
